/* eslint-disable no-mixed-spaces-and-tabs */
import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Link } from '@mui/icons-material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { showToast } from '@/features/toast/toastSlice';
import { useGetFolioEngageShareLinksQuery } from '@/features/deals/dealsApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { ENGAGE_REPORT_TYPES } from '@/utils/engageReports';

/**
 * Copies the newest active Folio Engage share link for one of `reportTypes` on this deal.
 *
 * @param {Object} props
 * @param {string[]} props.reportTypes - The ENGAGE_REPORT_TYPES keys this tab generates.
 * @returns {JSX.Element|null}
 */
const FolioEngageShareLinkButton = ({ reportTypes }) => {
	const dispatch = useDispatch();

	const { dealId } = useObjectContext();

	const { getFeatureFlags } = useFeatureFlagContext();

	const isFolioEngageEnabled = Boolean(getFeatureFlags('folioEngage')?.active);

	// The list is newest first. Links created before share links had a reportType all came
	// from the pre-renewal flow.
	const { shareLink } = useGetFolioEngageShareLinksQuery(dealId, {
		skip: !dealId || !isFolioEngageEnabled,
		selectFromResult: ({ data }) => ({
			shareLink: data?.find((link) =>
				reportTypes.includes(link.reportType ?? 'pre_renewal_report'),
			),
		}),
	});

	if (!shareLink?.engageShareLinkPath) return null;

	const shareLinkUrl = `${process.env.REACT_APP_ENGAGE_WEB_BASE_URL}${shareLink.engageShareLinkPath}`;
	const reportName = ENGAGE_REPORT_TYPES[shareLink.reportType ?? 'pre_renewal_report'].name;

	return (
		<CopyToClipboard
			text={shareLinkUrl}
			onCopy={() => {
				dispatch(
					showToast({
						message: `Copied the ${reportName} share link!`,
						success: true,
					}),
				);
			}}
		>
			<Tooltip title={`Copy the Folio Engage ${reportName} share link to clipboard`}>
				<span>
					<Button variant='tab_header_outlined' startIcon={<Link />}>
						{'Copy share link'}
					</Button>
				</span>
			</Tooltip>
		</CopyToClipboard>
	);
};
FolioEngageShareLinkButton.displayName = 'FolioEngageShareLinkButton';
export default FolioEngageShareLinkButton;
